'use client';

import { ChevronsUpDown, LogOut, Settings } from 'lucide-react';

import { Avatar, AvatarFallback, AvatarImage } from '@ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@ui/dropdown-menu';
import { SidebarMenu, SidebarMenuButton, SidebarMenuItem, useSidebar } from '@ui/sidebar';
import { useAuth } from '@components/auth/AuthContext';
import { ThemeToggle } from '@components/ThemeToggle';
import { useNavigate } from 'react-router-dom';

export function NavUser({
  user,
}: {
  user: {
    name: string;
    email: string;
    avatar: string;
  };
}) {
  const { isMobile } = useSidebar();
  const { logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const goToSettings = () => {
    navigate('/user-settings');
  };

  return (
    <SidebarMenu data-testid="user-menu">
      <SidebarMenuItem data-testid="user-menu-item">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <SidebarMenuButton
              size="lg"
              className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
              data-testid="user-dropdown-trigger"
            >
              <Avatar className="h-8 w-8 rounded-lg" data-testid="user-avatar">
                <AvatarImage src={user.avatar} alt={user.name} data-testid="user-avatar-image" />
                <AvatarFallback className="rounded-lg" data-testid="user-avatar-fallback">
                  {user.name.substring(0, 2).toUpperCase()}
                </AvatarFallback>
              </Avatar>
              <div className="grid flex-1 text-left text-sm leading-tight" data-testid="user-info">
                <span className="truncate font-semibold" data-testid="user-name">
                  {user.name}
                </span>
                <span className="truncate text-xs" data-testid="user-email">
                  {user.email}
                </span>
              </div>
              <ChevronsUpDown className="ml-auto size-4" data-testid="user-dropdown-chevron" />
            </SidebarMenuButton>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            className="w-[--radix-dropdown-menu-trigger-width] min-w-56 rounded-lg"
            side={isMobile ? 'bottom' : 'right'}
            align="end"
            sideOffset={4}
            data-testid="user-dropdown-content"
          >
            <DropdownMenuLabel className="p-0 font-normal" data-testid="user-dropdown-label">
              <div
                className="flex items-center gap-2 px-1 py-1.5 text-left text-sm"
                data-testid="user-dropdown-header"
              >
                <Avatar className="h-8 w-8 rounded-lg" data-testid="user-dropdown-avatar">
                  <AvatarImage
                    src={user.avatar}
                    alt={user.name}
                    data-testid="user-dropdown-avatar-image"
                  />
                  <AvatarFallback
                    className="rounded-lg"
                    data-testid="user-dropdown-avatar-fallback"
                  >
                    {user.name.substring(0, 2).toUpperCase()}
                  </AvatarFallback>
                </Avatar>
                <div
                  className="grid flex-1 text-left text-sm leading-tight"
                  data-testid="user-dropdown-info"
                >
                  <span className="truncate font-semibold" data-testid="user-dropdown-name">
                    {user.name}
                  </span>
                  <span className="truncate text-xs" data-testid="user-dropdown-email">
                    {user.email}
                  </span>
                </div>
              </div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator data-testid="user-dropdown-separator" />
            <ThemeToggle />
            <DropdownMenuSeparator data-testid="user-dropdown-theme-separator" />
            <DropdownMenuItem onClick={goToSettings} data-testid="user-settings-button">
              <Settings className="mr-2 h-4 w-4" data-testid="user-settings-icon" />
              <span data-testid="user-settings-text">User Settings</span>
            </DropdownMenuItem>
            <DropdownMenuItem onClick={handleLogout} data-testid="logout-button">
              <LogOut className="mr-2 h-4 w-4" data-testid="logout-icon" />
              <span data-testid="logout-text">Log out</span>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarMenuItem>
    </SidebarMenu>
  );
}
